import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import StudentReviewModal from './StudentReviewModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiPlus, FiTrash2, FiCheck, FiClock, FiLoader, FiMessageCircle, FiCheckCircle } = FiIcons;

const SessionModal = ({ student, onClose }) => {
  const { 
    months, 
    getStudentSessions, 
    addSession, 
    updateSessionPayment, 
    formatCurrency, 
    removeSession,
    studentReviews
  } = useData();
  
  const { user } = useAuth();
  const isTutor = user?.role === 'tutor';

  const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [selectedDate, setSelectedDate] = useState('');
  const [sessionTime, setSessionTime] = useState('16:00');
  const [sessionDuration, setSessionDuration] = useState(student.classDuration || '60');
  const [customDuration, setCustomDuration] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [sessionSubject, setSessionSubject] = useState('');
  const [sessionTutor, setSessionTutor] = useState('');
  const [sessionFee, setSessionFee] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedbackSession, setFeedbackSession] = useState(null);

  const studentSessions = getStudentSessions(student.id);
  const availableSubjects = student.subjects || [];

  useEffect(() => {
    if (sessionSubject) {
      const subData = availableSubjects.find(s => s.subject === sessionSubject);
      if (subData) {
        setSessionTutor(subData.tutor);
        setSessionFee(subData.feePerSession);
      }
    }
  }, [sessionSubject]);

  const handleAdd = async () => {
    if (!selectedDate || !sessionSubject) return;
    const duration = isCustom ? customDuration : sessionDuration;
    if (!duration || isNaN(duration)) return alert("Please enter a valid duration");
    
    setIsSubmitting(true);
    try {
      await addSession(student.id, selectedMonth, {
        date: selectedDate,
        time: sessionTime,
        duration: parseInt(duration),
        subject: sessionSubject,
        tutor: sessionTutor,
        feePerSession: sessionFee,
        isPaid: false
      });
      setSelectedDate('');
      setSessionSubject('');
      setIsCustom(false);
      setCustomDuration('');
    } catch (error) {
      alert("Failed to add: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId(null), 3000);
      return;
    }
    setIsDeleting(true);
    try {
      const res = await removeSession(id);
      if (!res.success) throw new Error(res.error);
      setConfirmDeleteId(null);
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasFeedback = (sessionId) => {
    return studentReviews.some(r => r.sessionId === sessionId);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tight">Sessions Hub</h2>
            <p className="text-indigo-600 font-bold">{student.studentName} • Grade {student.grade}</p>
          </div>
          <button onClick={onClose} className="p-4 hover:bg-gray-100 rounded-2xl transition-all">
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          <div className="bg-gray-900 rounded-[2rem] p-8 mb-10 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-indigo-400 mb-6 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="text-white text-sm" />
                Schedule Completed Class
              </h3>
              <div className={`grid grid-cols-1 md:grid-cols-2 ${isTutor ? 'lg:grid-cols-5' : 'lg:grid-cols-6'} gap-6`}>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase">Month</label>
                  <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-full bg-gray-800 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500">
                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase">Date</label>
                  <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full bg-gray-800 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase">Subject</label>
                  <select value={sessionSubject} onChange={(e) => setSessionSubject(e.target.value)} className="w-full bg-gray-800 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500">
                    <option value="">Select...</option>
                    {availableSubjects.map((s, i) => <option key={i} value={s.subject}>{s.subject}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase">Duration</label>
                  {!isCustom ? (
                    <select value={sessionDuration} onChange={(e) => e.target.value === 'custom' ? setIsCustom(true) : setSessionDuration(e.target.value)} className="w-full bg-gray-800 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500" >
                      <option value="30">30 min</option>
                      <option value="60">60 min</option>
                      <option value="90">90 min</option>
                      <option value="custom">Custom...</option>
                    </select>
                  ) : (
                    <div className="relative">
                      <input type="number" placeholder="Mins" value={customDuration} onChange={(e) => setCustomDuration(e.target.value)} className="w-full bg-indigo-900/50 border border-indigo-500/50 rounded-xl p-3 text-sm pr-10" />
                      <button onClick={() => setIsCustom(false)} className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400"><SafeIcon icon={FiX} /></button>
                    </div>
                  )}
                </div>
                {!isTutor && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase">Fee</label>
                    <input type="number" value={sessionFee} onChange={(e) => setSessionFee(e.target.value)} className="w-full bg-gray-800 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500" />
                  </div>
                )}
                <div className="flex items-end">
                  <button onClick={handleAdd} disabled={isSubmitting || !selectedDate || !sessionSubject} className="w-full bg-indigo-500 hover:bg-indigo-400 text-white p-3 rounded-xl font-black transition-all disabled:opacity-30 flex items-center justify-center gap-2" >
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiPlus} /> ADD</>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-12">
            {months.map(month => {
              const items = studentSessions[month] || [];
              if (items.length === 0) return null;
              return (
                <div key={month}>
                  <div className="flex items-center gap-4 mb-6">
                    <h4 className="text-xl font-black text-gray-900">{month}</h4>
                    <div className="h-px flex-1 bg-gray-100"></div>
                    <span className="text-xs font-black text-gray-400">{items.length} CLASSES</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AnimatePresence mode='popLayout'>
                      {items.sort((a,b) => new Date(a.date) - new Date(b.date)).map(session => (
                        <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} key={session.id} className="bg-white border-2 border-gray-50 rounded-[2rem] p-6 hover:shadow-xl transition-all group relative overflow-hidden" >
                          <div className="flex items-center gap-4 mb-6">
                            <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center ${session.isPaid ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>
                              <span className="text-[10px] font-black uppercase leading-none mb-1">{new Date(session.date).toLocaleString('default', { month: 'short' })}</span>
                              <span className="text-2xl font-black leading-none">{new Date(session.date).getDate()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-black text-gray-900 truncate text-lg">{session.subject}</h5>
                              <div className="text-xs text-gray-400 font-bold flex items-center gap-2">
                                <SafeIcon icon={FiClock} /> {session.time} • {session.duration}m
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                            <div className="flex items-center gap-2">
                               <button 
                                onClick={() => setFeedbackSession(session)}
                                className={`p-2 rounded-xl transition-all flex items-center gap-2 ${hasFeedback(session.id) ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-50 text-gray-400 hover:text-indigo-600'}`}
                                title="Session Report"
                               >
                                {hasFeedback(session.id) ? <SafeIcon icon={FiCheckCircle} /> : <SafeIcon icon={FiMessageCircle} />}
                                {hasFeedback(session.id) && <span className="text-[9px] font-black uppercase">Reported</span>}
                              </button>
                             
                              {!isTutor && (
                                <span className="text-sm font-black text-gray-900 ml-2">
                                  {formatCurrency(session.feePerSession)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {!isTutor && (
                                <button onClick={() => updateSessionPayment(session.id, !session.isPaid)} className={`p-2 rounded-xl transition-all ${session.isPaid ? 'text-emerald-500 bg-emerald-50' : 'text-gray-300 hover:bg-gray-100'}`}><SafeIcon icon={FiCheck} /></button>
                              )}
                              <button onClick={() => handleDelete(session.id)} disabled={isDeleting && confirmDeleteId === session.id} className={`p-2 rounded-xl transition-all flex items-center gap-2 ${confirmDeleteId === session.id ? 'bg-rose-500 text-white px-4' : 'text-rose-300 hover:bg-rose-50'}`} >
                                {isDeleting && confirmDeleteId === session.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiTrash2} />}
                                {confirmDeleteId === session.id && !isDeleting && <span className="text-[10px] font-black uppercase">Click again</span>}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {feedbackSession && (
          <StudentReviewModal 
            student={student} 
            session={feedbackSession} 
            onClose={() => setFeedbackSession(null)} 
          />
        )}
      </motion.div>
    </div>
  );
};

export default SessionModal;